import React, { useEffect, useState, useRef, useMemo } from "react";
import { sanitizedText } from "./utils/util";
import {
  Box,
  Stack,
  Text,
  VStack,
  HStack,
  Button,
  Pressable,
} from "native-base";
import UrlPreview from "./UrlPreview";
import { Loc } from "../../../App/Loc";
import { TouchableOpacity } from "react-native";

type PostTextProps = {
  item: any;
  preferencesLang?: string;
  currentUserId: string;
  setClickedUrl?: React.Dispatch<React.SetStateAction<string>>;
  setShowConfirmLinkPopup?: React.Dispatch<React.SetStateAction<boolean>>;
};

const PostText: React.FC<PostTextProps> = ({
  item,
  preferencesLang = "undefined",
  currentUserId,
  setClickedUrl,
  setShowConfirmLinkPopup,
}) => {
  const [more, setMore] = useState(false);
  const postRef = useRef<HTMLDivElement | null>(null);

  // Add isolated click listener (only for this post)
  useEffect(() => {
    const el = postRef.current;
    if (!el) return;

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName.toLowerCase() === "a") {
        e.preventDefault();
        e.stopPropagation(); // Stop bubbling to other instances
        const url = (target as HTMLAnchorElement).href;
        if (url) {
          setClickedUrl?.(url);
          setShowConfirmLinkPopup?.(true);
        }
      }
    };

    el.addEventListener("click", handleLinkClick);
    return () => el.removeEventListener("click", handleLinkClick);
  }, []);

  const htmlPostbgImg = useMemo(() => {
    if (!item?.text) return false;
    return /background-image\s*:\s*(?!\s*(none|unset)\b)[^;]+/i.test(item.text);
  }, [item?.text]);

  if (!item?.text) return <></>; // early return after hook

  const getBaseLang = (lang: any) => lang?.split("-")[0] || "";
  const isTranslated = item?.isTranslateText ?? false;
  let text = item.text;

  // Apply translation if available
  if (
    isTranslated &&
    Array.isArray(item?.trans) &&
    item.trans.length > 0 &&
    item?.owner?.id !== currentUserId
  ) {
    const userLang = getBaseLang(preferencesLang);
    const matchedTranslation = item.trans.find(
      (x: any) => getBaseLang(x?.lang) === userLang,
    );
    text = matchedTranslation?.text || text;
  }

  const extracted = sanitizedText(text);
  const postText = extracted?.outputString;

  const words = postText ? postText.trim().split(/\s+/) : [];
  const wordCount = words.length;
  const truncatedText = wordCount > 70 ? words.slice(0, 66).join(" ") : "";

  // HTML post
  if (item.ptype === "H") {
    //// SHOW MORE / SHOW LESS LOGIC
    //const extractedPlain = sanitizedText(text)?.outputString ?? "";
    //const htmlWords = extractedPlain.trim().split(/\s+/);
    //const htmlWordCount = htmlWords.length;
    //
    //// build a simple truncated HTML version
    //let shortHTML = text;
    //if (htmlWordCount > 70) {
    //  const limited = htmlWords.slice(0, 66).join(" ");
    //  shortHTML = `
    //      <div>
    //        ${limited}
    //      <span id="toggleMore" style="color:#32AE78; cursor:pointer;">... more</span>
    //      </div>
    //    `;
    //}
    //
    //let fullHTML = `
    //  <div>
    //    ${text}
    //  <span id="toggleLess" style="color:#32AE78; cursor:pointer;"> less</span>
    //  </div>
    //`;

    let finalHTML = text; //more ? fullHTML : shortHTML;

    if (htmlPostbgImg) {
      finalHTML = finalHTML.replace(
        /<div([^>]*)style="([^"]*)"/,
        (match, p1, p2) => {
          if (!/height\s*:/.test(p2)) {
            if (
              !/background\s*:\s*(none|unset)/i.test(p2) &&
              !/background-image\s*:\s*(?!\s*(none|unset)\b)[^;]+/i.test(p2)
            ) {
              return `<div${p1} style="${p2}; height: 480px"`;
            }
          }
          return match;
        },
      );
    }

    let divWidth = "100%";
    let divHeight = "auto";

    finalHTML = finalHTML.replace(/<figure[^>]*>(.*?)<\/figure>/g, "$1");

    // Extract width & height from the <div> inline style
    const divStyleMatch = finalHTML.match(
      /<div[^>]*style="[^"]*width\s*:\s*(\d+)px;[^"]*height\s*:\s*(\d+)px;?[^"]*"/i,
    );

    if (divStyleMatch) {
      divWidth = `${divStyleMatch[1]}px`;
      divHeight = `${divStyleMatch[2]}px`;
    }

    const shouldTruncate = wordCount > 70;

    return (
      <Box width="100%">
        <div
          ref={postRef}
          className="htmlPost"
          title="post"
          //style={{
          //  maxHeight: "max-content",
          //  width: htmlPostbgImg ? divWidth : "100%",
          //  minWidth: htmlPostbgImg ? divWidth : "100%",
          //  outline: "none", // remove focus outline
          //  userSelect: "text", // allow text selection
          //  cursor: "default",
          //}}
          style={{
            maxHeight: !more && shouldTruncate ? "220px" : "none",
            overflow: "hidden",
            position: "relative",
            userSelect: "text",
          }}
          contentEditable={htmlPostbgImg}
          suppressContentEditableWarning={true} // React warning suppress
          onInput={(e) => e.preventDefault()} // prevent editing
          onKeyDown={(e) => e.preventDefault()} // prevent typing
          onPaste={(e) => e.preventDefault()} // prevent pasting
          onFocus={(e) => (e.target as HTMLElement).blur()} // prevent caret focus
          dangerouslySetInnerHTML={{ __html: finalHTML }}
          //onClick={(e) => {
          //  const t = e.target as HTMLElement;
          //  if (t.id === "toggleMore") setMore(true);
          //  if (t.id === "toggleLess") setMore(false);
          //}}
        />

        {/* Fade + More */}
        {!more && shouldTruncate && (
          //<Box
          //  position="absolute"
          //  bottom={0}
          //  left={0}
          //  right={0}
          //  pt="40px"
          //  bg="linear-gradient(to top, white, transparent)"
          //>
          <Pressable onPress={() => setMore(true)}>
            <Text color="#32AE78" pl={1}>
              {Loc.currentLang.menu.seeMore}
            </Text>
          </Pressable>
          //</Box>
        )}

        {more && (
          <Pressable onPress={() => setMore(false)}>
            <Text color="#32AE78" mt={1}>
              {Loc.currentLang.menu.seeLess}
            </Text>
          </Pressable>
        )}
      </Box>
    );
  }

  // Normal text post
  return (
    <Stack justifyContent="center" width="100%">
      <Text>
        {!more && wordCount > 70 ? `${truncatedText}... ` : postText}
        {wordCount > 70 && (
          <TouchableOpacity onPress={() => setMore(!more)}>
            <Text color="#32AE78">{more ? " less" : " more"}</Text>
          </TouchableOpacity>
        )}
      </Text>

      {extracted?.urls?.length > 0 &&
        extracted.urls.map((url, i) => (
          <Box key={i}>
            <UrlPreview
              url={url}
              setClickedUrl={setClickedUrl}
              setShowConfirmLinkPopup={setShowConfirmLinkPopup}
            />
          </Box>
        ))}
    </Stack>
  );
};

export default PostText;
